import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import {
  FiFileText, FiRefreshCcw, FiLayers, FiBarChart2,
  FiGlobe, FiCpu, FiChevronDown, FiCheck, FiZap,
  FiShoppingCart, FiPrinter, FiSmartphone, FiDatabase, FiArrowRight, FiX
} from 'react-icons/fi';

/* ── Glowing Hero Background ── */
const HeroGlow = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-400/20 rounded-full blur-[120px]" />
    <div className="absolute top-[20%] right-[-10%] w-[30%] h-[50%] bg-indigo-400/20 rounded-full blur-[120px]" />
    <div className="absolute bottom-[-20%] left-[20%] w-[50%] h-[40%] bg-sky-300/20 rounded-full blur-[120px]" />
    <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
  </div>
);

/* ── Premium Fake Invoice Component ── */
const FakeInvoice = () => {
  const items = [
    { name: 'Pro Plan - Monthly', qty: 1, rate: 2499, amt: 2499 },
    { name: 'AI Credits', qty: 120, rate: 5, amt: 600 },
    { name: 'Team Member Seats', qty: 4, rate: 399, amt: 1596 },
  ];

  const subtotal = items.reduce((sum, item) => sum + item.amt, 0);
  const cgst = Math.round(subtotal * 0.09);
  const sgst = Math.round(subtotal * 0.09);
  const total = subtotal + cgst + sgst;

  return (
    <motion.div
      initial={{ rotateY: 15, rotateX: 10, z: 0, opacity: 0, y: 40 }}
      animate={{ rotateY: -5, rotateX: 5, z: 50, opacity: 1, y: 0 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
      style={{ perspective: 1000 }}
      className="relative z-10 w-full max-w-md mx-auto group"
    >
      {/* Decorative backdrop shadow glow */}
      <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-[2rem] transform group-hover:bg-blue-500/30 transition-all duration-500"></div>

      <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white overflow-hidden w-full transition-transform duration-500 group-hover:-translate-y-2">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-6 py-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-1 font-medium">Tax Invoice</p>
              <p className="font-semibold text-xl tracking-tight">NovaTech Solutions</p>
              <p className="text-[10px] opacity-75 mt-1 font-medium">GSTIN: 33ABCPF1234Q1Z5</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-lg font-medium opacity-90">#INV-00921</p>
              <p className="text-[10px] opacity-75 mt-1 font-medium">21 May 2026</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="text-[10px] text-slate-400 font-semibold tracking-wider grid grid-cols-12 gap-x-2 mb-3 pb-2 border-b border-slate-100">
            <div className="col-span-6">ITEM</div>
            <div className="col-span-2 text-right">QTY</div>
            <div className="col-span-2 text-right">RATE</div>
            <div className="col-span-2 text-right">AMOUNT</div>
          </div>

          <div className="space-y-4 text-sm mb-6">
            {items.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-x-2 items-center">
                <div className="col-span-6 font-medium text-slate-800">
                  {item.name}
                </div>
                <div className="col-span-2 text-right text-slate-500 font-medium">{item.qty}</div>
                <div className="col-span-2 text-right text-slate-500 font-medium">₹{item.rate}</div>
                <div className="col-span-2 text-right font-semibold text-slate-900">
                  ₹{item.amt.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="pt-4 border-t border-dashed border-slate-200 space-y-2 text-sm">
            <div className="flex justify-between text-slate-500 font-medium">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-slate-500 font-medium">
              <span>CGST (9%)</span>
              <span>₹{cgst.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-slate-500 font-medium">
              <span>SGST (9%)</span>
              <span>₹{sgst.toLocaleString()}</span>
            </div>

            <div className="flex justify-between text-xl font-semibold text-slate-900 pt-4 mt-2 border-t border-slate-100 items-end">
              <span>Total</span>
              <span className="text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">₹{total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50/50 backdrop-blur-sm px-6 py-4 flex items-center justify-between border-t border-slate-100">
          <span className="text-[10px] font-medium text-slate-400 tracking-wider">SECURE PAYMENTS BY BILLINGSOFTWARE</span>
          <span className="bg-emerald-100/80 text-emerald-700 border border-emerald-200/50 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-sm">
            <FiCheck /> PAID
          </span>
        </div>
      </div>
    </motion.div>
  );
};


/* ── Bento Grid Feature Card ── */
const BentoCard = ({ icon, title, desc, colSpan = "col-span-1", delay = 0, bgImage = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.6 }}
    className={`${colSpan} relative overflow-hidden group bg-white border border-slate-200/60 rounded-3xl p-8 hover:border-blue-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300`}
  >
    {bgImage && (
      <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none"
        style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    )}
    <div className="relative z-10">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-100 flex items-center justify-center text-blue-600 text-xl mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-3 tracking-tight">{title}</h3>
      <p className="text-slate-500 font-medium leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

/* ── FAQ ── */
const Faq = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      {faqs.map((f, i) => (
        <div key={i} className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden hover:border-blue-200 hover:shadow-md transition-all duration-300">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none">
            <span className="font-medium text-slate-900 pr-8">{f.q}</span>
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${open === i ? 'bg-blue-100 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
              <FiChevronDown className={`transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
            </div>
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="px-6 pb-6 pt-2">
                  <p className="text-slate-500 font-medium leading-relaxed">{f.a}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function BillingSoftware() {
  useEffect(() => window.scrollTo(0, 0), []);

  const bentoFeatures = [
    { icon: <FiFileText />, title: 'Smart GST Filing', desc: 'Auto-calculate CGST, SGST, and IGST. Generate GSTR-1, 2A, 3B, and 9 instantly without a CA.', colSpan: 'md:col-span-2' },
    { icon: <FiSmartphone />, title: 'Mobile POS', desc: 'Full-featured Android app. Scan barcodes and bill customers directly from your smartphone.', colSpan: 'md:col-span-1' },
    { icon: <FiGlobe />, title: 'Multi-branch Sync', desc: 'Manage unlimited retail locations from a single dashboard with real-time inventory tracking.', colSpan: 'md:col-span-1' },
    { icon: <FiDatabase />, title: 'Inventory Automation', desc: 'Stock auto-deducts upon billing. Get low-stock alerts and manage batch & expiry dates effortlessly.', colSpan: 'md:col-span-2' },
  ];

  const faqs = [
    { q: 'Can I use this on my phone at my shop counter?', a: 'Yes — our Android app works as a full POS terminal. Scan barcodes, bill customers, and print thermal receipts without a laptop.' },
    { q: 'Does it handle GST filing automatically?', a: 'Yes, GSTR-1 data is prepared automatically from your invoices. You review and file directly from the dashboard with a single click.' },
    { q: 'Can I manage multiple branches?', a: 'Yes, create unlimited branches under one account. Each branch has its own inventory, GST number, and report — all visible from the owner dashboard.' },
    { q: 'Is there a free trial?', a: 'Yes — 30 days completely free, no credit card required. Full access to every feature including GST reports and POS mode.' },
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] font-sans selection:bg-blue-200 selection:text-blue-900 overflow-x-hidden">
      <div className="bg-black text-white"><Header /></div>

      {/* ── HERO: Modern AI SaaS ── */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 px-6 overflow-hidden flex items-center justify-center min-h-[90vh]">
        <HeroGlow />

        <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-blue-100 shadow-[0_2px_10px_rgb(0,0,0,0.02)] mb-8">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              <span className="text-xs font-semibold text-blue-700 tracking-widest uppercase">Trusted by 15,000+ Businesses</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-semibold text-slate-900 tracking-tighter mb-8 leading-[1.05]">
              Bill fast.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">Grow faster.</span>
            </h1>

            <p className="text-slate-500 font-medium text-xl md:text-2xl mb-10 leading-relaxed max-w-lg">
              The AI-powered GST billing platform that practically runs your business for you. No accountant required.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="https://billpilot.vivekasoftek.com/" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-medium px-8 py-4 rounded-xl text-lg transition-all shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:-translate-y-0.5 flex items-center justify-center gap-2 group">
                Start Free Trial <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/products#DemoForm" className="w-full sm:w-auto bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-medium px-8 py-4 rounded-xl text-lg transition-all shadow-sm hover:shadow flex items-center justify-center gap-2">
                <FiPrinter className="text-slate-400" /> Get Demo
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4 text-sm font-medium text-slate-400">
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-500" /> 30-day trial</span>
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-500" /> Free setup</span>
            </div>
          </motion.div>

          <div className="hidden lg:block w-full">
            <FakeInvoice />
          </div>
        </div>
      </section>

      {/* ── LOGO CLOUD ── */}
      <section className="py-10 border-y border-slate-100 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-semibold tracking-widest text-slate-400 uppercase mb-8">Integrated with the tools you already use</p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
            <span className="text-2xl font-semibold tracking-tighter text-slate-800">Razorpay</span>
            <span className="text-2xl font-semibold tracking-tighter text-slate-800">Tally<span className="text-rose-500 text-3xl leading-none">.</span></span>
            <span className="text-2xl font-semibold tracking-tighter text-slate-800">Shopify</span>
            <span className="text-2xl font-semibold tracking-tighter text-slate-800 flex items-center gap-1"><FiShoppingCart className="text-blue-500" /> WooCommerce</span>
            <span className="text-2xl font-semibold tracking-tighter text-slate-800 flex items-center gap-1"><FiSmartphone className="text-green-500" /> WhatsApp</span>
          </div>
        </div>
      </section>

      {/* ── BENTO GRID FEATURES ── */}
      <section className="py-24 px-6 relative">
        <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-blue-50/50 rounded-bl-[100px] pointer-events-none -z-10"></div>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight mb-6">Everything you need to <span className="text-blue-600">scale</span>.</h2>
            <p className="text-lg font-medium text-slate-500">Stop juggling multiple apps. Manage invoicing, inventory, and GST compliance from one beautiful interface.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bentoFeatures.map((f, i) => (
              <BentoCard key={i} {...f} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* ── BEFORE / AFTER ── */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        {/* Dark mode glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[150px] pointer-events-none"></div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">The difference is clear.</h2>
            <p className="text-xl font-medium text-slate-400">Why thousands of businesses upgraded from legacy software.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* The Old Way */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10 backdrop-blur-xl">
              <div className="inline-block bg-rose-500/20 text-rose-400 font-semibold px-4 py-2 rounded-full text-sm mb-8">The Old Way</div>
              <ul className="space-y-5">
                {[
                  'Manual data entry for GSTR-1 & 3B',
                  'Ugly, non-customizable invoices',
                  'No mobile app. Tied to the desk.',
                  'Stock mismatches and missing items',
                  'Separate software for POS & Accounting'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-slate-300 font-medium">
                    <FiX className="text-rose-500 text-xl shrink-0 mt-0.5" /> {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* The New Way */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-gradient-to-br from-blue-600/20 to-indigo-600/10 border border-blue-500/30 rounded-3xl p-8 md:p-10 backdrop-blur-xl relative overflow-hidden">
              {/* Shine effect */}
              <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-5 animate-[shine_4s_infinite]"></div>

              <div className="relative z-10">
                <div className="inline-block bg-blue-500 text-white font-semibold px-4 py-2 rounded-full text-sm mb-8 shadow-[0_0_15px_rgba(59,130,246,0.5)]">BillingSoftware</div>
                <ul className="space-y-5">
                  {[
                    'Auto-generated GST reports in 1 click',
                    'Beautiful, brand-matching invoices',
                    'Cloud-based with full Mobile POS App',
                    'Real-time automated inventory sync',
                    'All-in-one platform for your business'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4 text-white font-medium">
                      <FiCheck className="text-blue-400 text-xl shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 px-6 bg-white relative">
        <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
          <div className="w-full lg:w-1/3">
            <h2 className="text-3xl font-semibold text-slate-900 mb-4 tracking-tight">Got questions?</h2>
            <p className="text-slate-500 font-medium mb-8">We've got answers. If you can't find what you're looking for, our support team is just a click away.</p>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="font-semibold text-slate-900 mb-2">Still need help?</h3>
              <p className="text-sm text-slate-500 font-medium mb-4">Chat with our product experts to see if we're a good fit.</p>
              <button className="text-blue-600 font-medium text-sm flex items-center gap-1 hover:text-blue-700 transition-colors">
                Contact Support <FiArrowRight />
              </button>
            </div>
          </div>
          <div className="w-full lg:w-2/3">
            <Faq faqs={faqs} />
          </div>
        </div>
      </section>

      <div className="bg-black text-white"><Footer /></div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes shine {
          0% { left: -100%; }
          20% { left: 200%; }
          100% { left: 200%; }
        }
      `}} />
    </div>
  );
}
